import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { privateApi } from '../../../api/api';

// Skema untuk memvalidasi payload yang dikirim ke hook ini
export const SyncPropertiesPayloadSchema = z.object({
    user_ids: z.array(z.string().uuid()),
    property_ids: z.array(z.string().uuid()),
});

// Tipe TypeScript yang diturunkan dari skema
type SyncPropertiesPayload = z.infer<typeof SyncPropertiesPayloadSchema>;

// Skema untuk respons sukses dari API (diasumsikan sederhana)
const SyncPropertyResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.any().optional(),
});

type SyncPropertyResponse = z.infer<typeof SyncPropertyResponseSchema>;

// Endpoint API untuk operasi sync
const API_ENDPOINT = '/v4/user-property/sync';

/**
 * Hook kustom untuk menyinkronkan properti seorang pengguna (atau beberapa pengguna).
 * Menggantikan attach/detach dengan satu panggilan API deklaratif.
 *
 * @returns Hasil dari React Query `useMutation`.
 */
export const useSyncPropertyV4 = () => {
    const queryClient = useQueryClient();

    return useMutation<SyncPropertyResponse, Error, SyncPropertiesPayload>({
        /**
         * Fungsi mutasi yang akan dijalankan saat `mutate()` dipanggil.
         * @param payload - Objek yang berisi `user_ids` dan `property_ids`.
         */
        mutationFn: async (payload: SyncPropertiesPayload): Promise<SyncPropertyResponse> => {
            // Validasi payload di level komponen, tapi hook ini siap menerima data yang sudah tervalidasi.
            try {
                const response = await privateApi.post(API_ENDPOINT, payload);

                // Validasi struktur data respons dari API.
                const validationResult = SyncPropertyResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("API response validation failed in useSyncPropertyV4:", validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server after syncing property.");
                }

                return validationResult.data;
            } catch (error) {
                console.error('Failed to sync property (V4):', error);
                throw error;
            }
        },

        /**
         * Callback yang dijalankan setelah mutasi berhasil.
         * Invalidasi cache di sini sangat penting untuk menjaga UI tetap sinkron.
         */
        onSuccess: (_data, variables) => {
            const { user_ids } = variables;

            // 1. Invalidate semua query daftar pengguna.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'list'] });

            // 2. Invalidate query detail untuk setiap pengguna yang terpengaruh.
            if (user_ids && user_ids.length > 0) {
                user_ids.forEach(userId => {
                    queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'detail', userId] });
                });
            }

            // 3. Invalidate query daftar semua properti.
            // Ini akan menyegarkan daftar properti "tersedia" di dialog.
            queryClient.invalidateQueries({ queryKey: ['properties', 'v4', 'all'] });
        },
    });
};